import React, { useState, useEffect, useCallback } from 'react';
import { User, Users, Check, AlertCircle, Save, Loader, Shuffle, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { teamAPI } from '../services';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { apiCall } from '../../services/apiUtil';

/**
 * TeamFormation Component
 *
 * A reusable component for forming teams from a pool of available members.
 * Supports single, double, and team game types.
 * Can create 1 or multiple teams at a time.
 *
 * @param {Object} props
 * @param {Array} props.availableMembers - List of members to choose from
 *   Format: [{ id, name, firstName, lastName, username, profileImage }]
 * @param {string|number} props.ownerId - ID of the owner (squad/group/user)
 * @param {string} props.ownerType - Type of owner: 'group' | 'user'
 * @param {string} props.gameType - Game type: 'single' | 'double' | 'team'
 * @param {number} props.maxPlayersPerTeam - Max players per team (optional, defaults by gameType)
 * @param {number} props.minPlayersPerTeam - Min players per team (optional, defaults by gameType)
 * @param {number} props.teamCount - Number of teams (default: 2, use 1 for single team creation)
 * @param {Array} props.initialTeams - Initial teams for edit mode (optional)
 * @param {Object} props.teamColors - Custom team colors (optional)
 * @param {Function} props.onTeamsChange - Callback when teams change (for controlled mode)
 * @param {Function} props.onTeamsFormed - Callback when teams are confirmed/ready
 * @param {Function} props.onCancel - Callback when cancelled
 * @param {boolean} props.showConfirmButton - Show confirm button (default: false for inline use)
 * @param {string} props.confirmButtonText - Custom confirm button text
 * @param {React.ReactNode} props.actionButton - Custom action button to render instead of default
 * @param {boolean} props.saveToBackend - Whether to save teams to backend (default: true)
 * @param {boolean} props.showSaveToggle - Show toggle for save option (default: false)
 * @param {string} props.sportTypeId - Sport type ID for team creation
 * @param {Function} props.onSaveComplete - Callback when save completes with saved team data
 * @param {string} props.sportName - Sport name to display (if passed, shows as readonly)
 * @param {boolean} props.showSettingsInfo - Show sport and team type info section (default: false)
 * @param {boolean} props.showExistingTeams - Show option to select from existing teams (default: false)
 * @param {boolean} props.readOnly - Make teams read-only (no add/remove players) - for tournament games
 */
const TeamFormation = ({
  availableMembers = [],
  ownerId,
  ownerType = 'group',
  gameType = 'team',
  maxPlayersPerTeam: maxPlayersProp,
  minPlayersPerTeam: minPlayersProp,
  teamCount = 2,
  initialTeams = null,
  teamColors = null,
  onTeamsChange,
  onTeamsFormed,
  onCancel,
  showConfirmButton = false,
  confirmButtonText = 'Confirm Teams',
  actionButton = null,
  saveToBackend = true,
  showSaveToggle = false,
  sportTypeId = null,
  onSaveComplete,
  editTeamId = null,  // Team ID for edit mode
  existingMembers = [], // Existing team members for edit mode
  teamDescription = '', // Team description
  teamLogo = '🏆', // Team logo
  sportName = null, // Sport name to display (readonly when passed)
  showSettingsInfo = false, // Show sport and team type info section
  createdByUserId = null, // User ID who is creating the team (for captain)
  showExistingTeams = false, // Show option to select from existing teams
  readOnly = false, // Make teams read-only - for tournament games
  showReadOnlyMessage = false, // Show the "pre-set from tournament" banner
  availablePlayersCount = null, // If set, shows "X available" next to max players badge
}) => {
  const { theme } = useTheme();

  // Default colors for teams
  const defaultTeamColors = [
    { border: 'border-blue-400', accent: 'border-blue-500', text: 'text-blue-500', bg: 'bg-blue-500' },
    { border: 'border-red-400', accent: 'border-red-500', text: 'text-red-500', bg: 'bg-red-500' },
    { border: 'border-green-400', accent: 'border-green-500', text: 'text-green-500', bg: 'bg-green-500' },
    { border: 'border-purple-400', accent: 'border-purple-500', text: 'text-purple-500', bg: 'bg-purple-500' },
  ];

  const colors = teamColors || defaultTeamColors;

  // Determine player limits based on game type
  const getPlayerLimits = useCallback(() => {
    switch (gameType) {
      case 'single':
        return { min: 1, max: 1 };
      case 'double':
        return { min: 2, max: 2 };
      case 'team':
      default:
        return { min: minPlayersProp || 2, max: maxPlayersProp || 15 };
    }
  }, [gameType, maxPlayersProp, minPlayersProp]);

  const { min: minPlayers, max: maxPlayers } = getPlayerLimits();

  // Deduplicate players by id (keeps first occurrence)
  const dedupePlayers = (players) => {
    const seen = new Set();
    return players.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  };

  // Transform members to player format, filtering out deleted users
  const transformMembers = useCallback((memberList) => {
    const seen = new Set();
    return (memberList || [])
      .filter(member => {
        // Filter out deleted users FIRST
        const isDeleted = member.isDeletedUser ||
                         member.is_deleted ||
                         member.status === 'deleted';
        return !isDeleted;
      })
      .map(member => ({
        id: member.userId || member.id,
        name: member.username ||
              (member.firstName && member.lastName
                ? `${member.firstName} ${member.lastName}`
                : member.name || 'Unknown'),
        firstName: member.firstName,
        lastName: member.lastName,
        profileImage: member.profileImage || member.profilePicture,
        isCurrentUser: member.isCurrentUser || false,
      }))
      .filter(member => {
        // Remove duplicates
        if (seen.has(member.id)) return false;
        seen.add(member.id);
        return true;
      });
  }, []);

  // Initialize teams state
  const createInitialTeams = useCallback(() => {
    if (initialTeams && initialTeams.length > 0) {
      return initialTeams.map((t) => ({
        ...t,
        existingTeamId: t.existingTeamId || null,
      }));
    }

    const teams = [];
    for (let i = 0; i < teamCount; i++) {
      teams.push({
        name: `Team ${String.fromCharCode(65 + i)}`, // Team A, Team B, etc.
        players: [],
        existingTeamId: null, // Track if this slot uses an existing team
      });
    }
    return teams;
  }, [initialTeams, teamCount]);

  // State
  const [teams, setTeams] = useState(createInitialTeams);
  const [shouldSave, setShouldSave] = useState(saveToBackend);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [randomWarning, setRandomWarning] = useState(null);
  const [generateNames, setGenerateNames] = useState(false);
  const [isGeneratingNames, setIsGeneratingNames] = useState(false);
  const [existingTeamsList, setExistingTeamsList] = useState([]); // Existing teams from the group
  const [loadingExistingTeams, setLoadingExistingTeams] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState(() => {
    const allPlayers = transformMembers(availableMembers);

    // If initialTeams provided, filter out already assigned players
    if (initialTeams && initialTeams.length > 0) {
      const assignedIds = new Set();
      initialTeams.forEach(team => {
        team.players.forEach(p => assignedIds.add(p.id));
      });
      return allPlayers.filter(p => !assignedIds.has(p.id));
    }

    return allPlayers;
  });

  // Sync teams state when teamCount changes (user selected a different number of teams)
  useEffect(() => {
    if (initialTeams && initialTeams.length > 0) return; // edit mode — don't override
    setTeams(prevTeams => {
      if (prevTeams.length === teamCount) return prevTeams; // no change needed
      const next = [];
      for (let i = 0; i < teamCount; i++) {
        next.push(prevTeams[i] || { name: `Team ${String.fromCharCode(65 + i)}`, players: [], existingTeamId: null });
      }
      return next;
    });
  }, [teamCount]);

  // Update teams when initialTeams prop changes (for edit mode)
  // Use a ref to track previous value to avoid infinite loops when parent passes new array reference
  const prevInitialTeamsRef = React.useRef(null);

  useEffect(() => {
    if (!initialTeams || initialTeams.length === 0) return;

    // Compare with previous initialTeams to avoid unnecessary updates
    // This prevents infinite loops when parent passes new array reference with same content
    const currentTeamsJson = JSON.stringify(initialTeams.map(t => ({ name: t.name, playerIds: t.players.map(p => p.id).sort() })));
    const prevTeamsJson = prevInitialTeamsRef.current;

    if (currentTeamsJson === prevTeamsJson) {
      return; // No actual change, skip update
    }

    prevInitialTeamsRef.current = currentTeamsJson;
    setTeams(initialTeams);

    // Update available players to exclude assigned players
    const assignedIds = new Set();
    initialTeams.forEach(team => {
      team.players.forEach(p => assignedIds.add(p.id));
    });
    const allPlayers = transformMembers(availableMembers);
    setAvailablePlayers(allPlayers.filter(p => !assignedIds.has(p.id)));
  }, [initialTeams, availableMembers, transformMembers]);

  // Update available players when members prop changes
  useEffect(() => {
    if (!availableMembers || availableMembers.length === 0) return;

    // Get all currently assigned player IDs
    const assignedIds = new Set();
    teams.forEach(team => {
      team.players.forEach(p => assignedIds.add(p.id));
    });

    // Transform and filter
    const allPlayers = transformMembers(availableMembers);
    setAvailablePlayers(allPlayers.filter(p => !assignedIds.has(p.id)));
  }, [availableMembers, transformMembers]);

  // Sync shouldSave with saveToBackend prop when toggle is not shown
  useEffect(() => {
    if (!showSaveToggle) {
      setShouldSave(saveToBackend);
    }
  }, [saveToBackend, showSaveToggle]);

  // Fetch existing teams for the group when showExistingTeams is enabled
  // Filter to only show teams where ALL players are available (in availableMembers)
  useEffect(() => {
    const fetchExistingTeams = async () => {
      if (!showExistingTeams || !ownerId || !ownerType) {
        setExistingTeamsList([]);
        return;
      }

      setLoadingExistingTeams(true);
      try {
        const result = await teamAPI.getByOwner(ownerType, ownerId);
        const teamsList = Array.isArray(result) ? result : (result?.data || []);

        // Get set of available member IDs for quick lookup
        // Convert to numbers to ensure type consistency with team member IDs
        const availableMemberIds = new Set(
          (availableMembers || []).map(m => {
            const id = m.userId || m.id;
            return typeof id === 'string' ? parseInt(id, 10) : id;
          })
        );

        // Filter teams: all members must be in the available pool
        const filteredTeams = teamsList.filter(team => {
          const members = team.members || [];

          if (members.length === 0) return false;

          return members.every(member => {
            let memberId = member.userId || member.user_id || member.user?.id || member.id;
            memberId = typeof memberId === 'string' ? parseInt(memberId, 10) : memberId;
            return availableMemberIds.has(memberId);
          });
        });

        setExistingTeamsList(filteredTeams);
      } catch (error) {
        console.error('Failed to fetch existing teams:', error);
        setExistingTeamsList([]);
      } finally {
        setLoadingExistingTeams(false);
      }
    };

    fetchExistingTeams();
  }, [showExistingTeams, ownerId, ownerType, availableMembers, gameType]);

  // Select an existing team for a team slot
  const selectExistingTeam = useCallback((existingTeam, teamIndex) => {
    // Transform existing team members to player format
    const teamPlayers = (existingTeam.members || []).map(member => {

      // Handle various API response structures
      const user = member.user || {};
      const firstName = user.firstName || user.first_name || member.firstName || member.first_name || '';
      const lastName = user.lastName || user.last_name || member.lastName || member.last_name || '';
      const username = user.username || member.username || '';

      // Build name from available fields
      let name = 'Unknown';
      if (firstName && lastName) {
        name = `${firstName} ${lastName}`;
      } else if (firstName) {
        name = firstName;
      } else if (lastName) {
        name = lastName;
      } else if (username) {
        name = username;
      } else if (member.name) {
        name = member.name;
      }

      return {
        id: member.userId || member.user_id || user.id || member.id,
        name,
        firstName,
        lastName,
        profileImage: user.profileImage || user.profile_image || member.profileImage || member.profile_image,
      };
    });

    // Get team name
    const teamName = existingTeam.teamName || existingTeam.team_name || existingTeam.name || `Team ${String.fromCharCode(65 + teamIndex)}`;

    setTeams(prevTeams => {
      const newTeams = [...prevTeams];

      // Return previous players to available pool
      const previousPlayers = newTeams[teamIndex].players;

      newTeams[teamIndex] = {
        name: teamName,
        players: teamPlayers,
        existingTeamId: existingTeam.id, // Track which existing team was selected
      };

      // Update available players
      setAvailablePlayers(prev => {
        // Add back previous players, remove new team players
        const newPlayerIds = new Set(teamPlayers.map(p => p.id));
        const withPrevious = dedupePlayers([...prev, ...previousPlayers]);
        return withPrevious.filter(p => !newPlayerIds.has(p.id));
      });

      return newTeams;
    });
  }, []);

  // Clear existing team selection for a team slot
  const clearExistingTeamSelection = useCallback((teamIndex) => {
    setTeams(prevTeams => {
      const newTeams = [...prevTeams];
      const team = newTeams[teamIndex];

      // Return players to available pool
      setAvailablePlayers(prev => dedupePlayers([...prev, ...team.players]));

      newTeams[teamIndex] = {
        name: `Team ${String.fromCharCode(65 + teamIndex)}`,
        players: [],
        existingTeamId: null,
      };

      return newTeams;
    });
  }, []);

  // Check if teams are valid (meet minimum requirements)
  const areTeamsValid = useCallback(() => {
    return teams.every(team => team.players.length >= minPlayers);
  }, [teams, minPlayers]);

  // Notify parent of changes
  // Use a ref to track previous teams to avoid notifying when nothing changed
  const prevTeamsForCallbackRef = React.useRef(null);

  useEffect(() => {
    if (!onTeamsChange) return;

    // Compare with previous to avoid unnecessary callbacks that could cause loops
    // Include existingTeamId in comparison to detect when existing team is selected/cleared
    const currentTeamsJson = JSON.stringify(teams.map(t => ({
      name: t.name,
      playerIds: t.players.map(p => p.id).sort(),
      existingTeamId: t.existingTeamId || null
    })));
    if (currentTeamsJson === prevTeamsForCallbackRef.current) {
      return;
    }
    prevTeamsForCallbackRef.current = currentTeamsJson;

    onTeamsChange({
      teams: teams.map(t => ({
        ...t,
        existingTeamId: t.existingTeamId || null, // Ensure existingTeamId is always present
      })),
      gameType,
      ownerId,
      ownerType,
      isValid: areTeamsValid(),
    });
  }, [teams, gameType, ownerId, ownerType, onTeamsChange, areTeamsValid]);

  // Helper to get player's first name for team naming
  const getPlayerFirstName = (player) => {
    if (player.firstName) return player.firstName;
    // Try to extract first name from full name
    const fullName = player.name || '';
    const firstName = fullName.split(' ')[0];
    return firstName || 'Player';
  };

  // Generate team name based on players
  const generateTeamName = (players, teamIndex) => {
    if (players.length === 0) {
      return `Team ${String.fromCharCode(65 + teamIndex)}`; // Team A, Team B, etc.
    }
    if (players.length === 1) {
      // Single player: use their full name or first name
      return players[0].name || `${players[0].firstName || ''} ${players[0].lastName || ''}`.trim() || `Team ${String.fromCharCode(65 + teamIndex)}`;
    }
    // Multiple players: use first two players' first names
    return players.slice(0, 2).map(p => getPlayerFirstName(p)).join('/');
  };

  // Add player to a specific team
  const addPlayerToTeam = useCallback((player, teamIndex) => {
    setRandomWarning(null);
    setTeams(prevTeams => {
      const newTeams = [...prevTeams];
      const team = newTeams[teamIndex];

      // Check if team is full
      if (team.players.length >= maxPlayers) {
        return prevTeams;
      }

      // Check if player already in team
      if (team.players.some(p => p.id === player.id)) {
        return prevTeams;
      }

      const newPlayers = [...team.players, player];

      // For singles/doubles, auto-generate team name from players
      const newTeamName = maxPlayers <= 2
        ? generateTeamName(newPlayers, teamIndex)
        : team.name;

      newTeams[teamIndex] = {
        ...team,
        name: newTeamName,
        players: newPlayers,
      };

      return newTeams;
    });

    setAvailablePlayers(prev => prev.filter(p => p.id !== player.id));
  }, [maxPlayers]);

  // Remove player from a team
  const removePlayerFromTeam = useCallback((player, teamIndex) => {
    setTeams(prevTeams => {
      const newTeams = [...prevTeams];
      const remainingPlayers = newTeams[teamIndex].players.filter(p => p.id !== player.id);

      // For singles/doubles mode, regenerate team name based on remaining players
      const newTeamName = maxPlayers <= 2
        ? generateTeamName(remainingPlayers, teamIndex)
        : newTeams[teamIndex].name;

      newTeams[teamIndex] = {
        ...newTeams[teamIndex],
        name: newTeamName,
        players: remainingPlayers,
      };
      return newTeams;
    });

    setAvailablePlayers(prev => dedupePlayers([...prev, player]));
  }, [maxPlayers]);

  // When maxPlayers decreases (e.g. switching from team→double or team→single),
  // trim excess players from each team and return them to the available pool.
  useEffect(() => {
    setTeams(prevTeams => {
      const returned = [];
      const trimmed = prevTeams.map(team => {
        if (team.players.length <= maxPlayers) return team;
        returned.push(...team.players.slice(maxPlayers));
        return { ...team, players: team.players.slice(0, maxPlayers) };
      });
      if (returned.length > 0) {
        setAvailablePlayers(prev => dedupePlayers([...prev, ...returned]));
      }
      return trimmed;
    });
  }, [maxPlayers]);

  // Check if random distribution is possible (at least 1 player per team)
  const canRandomlyDistribute = useCallback(() => {
    const assignedPlayers = teams.flatMap(t => t.players);
    const totalPlayers = availablePlayers.length + assignedPlayers.length;
    return totalPlayers >= teamCount;
  }, [availablePlayers, teams, teamCount]);

  // Randomly distribute players across teams
  const randomlyDistributePlayers = useCallback(() => {
    setRandomWarning(null);

    // Gather all players (available + currently assigned)
    const assignedPlayers = teams.flatMap(t => t.players);
    const allPlayers = [...availablePlayers, ...assignedPlayers];

    // Warn if not enough players to meet minimum per team
    const requiredPlayers = teamCount * minPlayers;
    if (allPlayers.length < requiredPlayers) {
      setRandomWarning(`Not enough players — need at least ${requiredPlayers} to fill ${teamCount} teams (${minPlayers} per team), but only ${allPlayers.length} available.`);
      return;
    }

    // Shuffle the players array (Fisher-Yates algorithm)
    const shuffled = [...allPlayers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Distribute players evenly — each team gets the same count, capped at maxPlayers
    const playersPerTeam = Math.min(Math.floor(shuffled.length / teamCount), maxPlayers);
    const newTeams = [];

    for (let i = 0; i < teamCount; i++) {
      const teamPlayers = shuffled.slice(i * playersPerTeam, (i + 1) * playersPerTeam);
      newTeams.push({
        name: maxPlayers <= 2 ? generateTeamName(teamPlayers, i) : `Team ${String.fromCharCode(65 + i)}`,
        players: teamPlayers,
      });
    }

    // Any leftover players (due to odd counts) go back to available pool
    const remainingPlayers = shuffled.slice(teamCount * playersPerTeam);

    setTeams(newTeams);
    setAvailablePlayers(remainingPlayers);
  }, [availablePlayers, teams, teamCount, minPlayers, maxPlayers]);

  // Update team name
  const updateTeamName = useCallback((teamIndex, name) => {
    setTeams(prevTeams => {
      const newTeams = [...prevTeams];
      newTeams[teamIndex] = {
        ...newTeams[teamIndex],
        name,
      };
      return newTeams;
    });
  }, []);

  // Save a single team to backend (create or update)
  const saveTeamToBackend = useCallback(async (team) => {
    // For createdBy, use the explicit prop or find current user from team players
    const currentUserPlayer = team.players.find(p => p.isCurrentUser);
    const captainId = createdByUserId || currentUserPlayer?.id || team.players[0]?.id;

    const teamData = {
      name: team.name,
      description: teamDescription || team.description || '',
      logo: teamLogo || team.logo || '🏆',
      maxPlayers: maxPlayers,
      createdBy: captainId,  // Use actual user ID for captain
      ownerType: ownerType,
      ownerId: String(ownerId),  // Ensure ownerId is a string
    };

    // Add sport type if provided
    if (sportTypeId) {
      teamData.sportTypeId = sportTypeId;
    }

    let savedTeam;

    if (editTeamId) {
      // Edit mode: update existing team
      const updateData = {
        teamName: team.name,
        description: teamDescription || team.description || '',
        teamLogo: teamLogo || team.logo || '🏆',
        maxMembers: maxPlayers,
      };
      if (sportTypeId) {
        updateData.sportTypeId = sportTypeId;
      }

      const result = await teamAPI.update(editTeamId, updateData);
      savedTeam = result?.data || result;
      savedTeam.id = editTeamId;

      // Sync members: determine who to add and who to remove
      // Normalize IDs to strings to avoid number/string type mismatch in Set lookups
      const currentPlayerIds = new Set(team.players.map(p => String(p.id)));
      const existingMemberIds = new Set(existingMembers.map(m => String(m.id)));

      // Remove players that are no longer in the team
      for (const existingMember of existingMembers) {
        if (!currentPlayerIds.has(String(existingMember.id))) {
          await teamAPI.removePlayer(editTeamId, existingMember.id);
        }
      }

      // Add new players
      for (const player of team.players) {
        if (!existingMemberIds.has(String(player.id))) {
          await teamAPI.addPlayer(editTeamId, {
            userId: player.id,
            firstName: player.firstName,
            lastName: player.lastName,
          });
        }
      }
    } else {
      // Create mode: create new team
      const result = await teamAPI.create(teamData);
      savedTeam = result?.data || result;

      // Add members to the team if there are players
      if (team.players && team.players.length > 0 && savedTeam?.id) {
        for (const player of team.players) {
          try {
            await teamAPI.addPlayer(savedTeam.id, {
              userId: player.id,
              firstName: player.firstName,
              lastName: player.lastName,
            });
          } catch (err) {
            console.error(`Failed to add player ${player.id} to team:`, err);
          }
        }
      }
    }

    return savedTeam;
  }, [editTeamId, existingMembers, maxPlayers, sportTypeId, ownerType, ownerId, teamDescription, teamLogo, createdByUserId]);

  // Handle confirm
  const handleConfirm = useCallback(async () => {
    if (!areTeamsValid()) return;

    setSaveError(null);

    const teamsData = {
      teams,
      gameType,
      ownerId,
      ownerType,
    };

    // If saving to backend
    if (shouldSave) {
      setIsSaving(true);
      try {
        const savedTeams = [];
        for (const team of teams) {
          const savedTeam = await saveTeamToBackend(team);
          savedTeams.push({
            ...team,
            savedId: savedTeam?.id,
            savedData: savedTeam,
          });
        }

        // Notify about save completion
        if (onSaveComplete) {
          onSaveComplete(savedTeams);
        }

        // Notify teams formed with saved data
        if (onTeamsFormed) {
          onTeamsFormed({
            ...teamsData,
            teams: savedTeams,
            saved: true,
          });
        }
      } catch (error) {
        console.error('Failed to save teams:', error);
        setSaveError(error.message || 'Failed to save teams');
        setIsSaving(false);
        return;
      }
      setIsSaving(false);
    } else {
      // Just return the teams without saving
      if (onTeamsFormed) {
        onTeamsFormed({
          ...teamsData,
          saved: false,
        });
      }
    }
  }, [teams, gameType, ownerId, ownerType, shouldSave, onTeamsFormed, onSaveComplete, areTeamsValid, maxPlayers, sportTypeId, saveTeamToBackend]);

  // Check if single player mode
  const isSinglePlayerMode = maxPlayers === 1;

  // Render a single team card
  const renderTeamCard = (team, teamIndex) => {
    const teamColor = colors[teamIndex % colors.length];
    const isFull = team.players.length >= maxPlayers;

    return (
      <div
        className={`${theme.backgroundSecondary} rounded-xl p-4 border-2 ${teamColor.border}`}
      >
        {/* Existing Team Selection - shown when showExistingTeams is enabled, not read-only, and not singles mode */}
        {showExistingTeams && !readOnly && !isSinglePlayerMode && (
          <div className="mb-4">
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>
              Select Existing Team
            </label>
            {loadingExistingTeams ? (
              <p className={`text-xs ${theme.textMuted} py-2`}>Loading teams...</p>
            ) : existingTeamsList.length === 0 ? (
              <p className={`text-xs ${theme.textMuted} py-2`}>No saved teams found for this game type.</p>
            ) : (
              <div className="flex gap-2">
                <select
                  value={team.existingTeamId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (selectedId) {
                      const existingTeam = existingTeamsList.find(t => String(t.id) === selectedId);
                      if (existingTeam) {
                        selectExistingTeam(existingTeam, teamIndex);
                      }
                    }
                  }}
                  className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-sm`}
                >
                  <option value="">-- Select existing team --</option>
                  {(() => {
                      const seen = new Set();
                      return existingTeamsList
                        .filter(t => {
                          if (seen.has(t.id)) return false;
                          seen.add(t.id);
                          const isUsedElsewhere = teams.some((team, idx) =>
                            idx !== teamIndex && team.existingTeamId === t.id
                          );
                          return !isUsedElsewhere;
                        })
                        .map(existingTeam => (
                          <option key={existingTeam.id} value={existingTeam.id}>
                            {existingTeam.teamName || existingTeam.team_name || existingTeam.name}
                            {existingTeam.members?.length > 0 && ` (${existingTeam.members.length} players)`}
                          </option>
                        ));
                    })()}
                </select>
                {team.existingTeamId && (
                  <button
                    type="button"
                    onClick={() => clearExistingTeamSelection(teamIndex)}
                    className="px-3 py-2 text-red-500 hover:text-red-700 text-sm font-medium"
                    title="Clear selection"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            {team.existingTeamId && (
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                Using existing team - players loaded automatically
              </p>
            )}
          </div>
        )}

        {/* Divider when both existing teams and manual selection are shown (not for singles) */}
        {showExistingTeams && !readOnly && existingTeamsList.length > 0 && !team.existingTeamId && !isSinglePlayerMode && (
          <div className="flex items-center gap-2 mb-4">
            <div className={`flex-1 border-t ${theme.border}`}></div>
            <span className={`text-xs ${theme.textMuted}`}>or create new</span>
            <div className={`flex-1 border-t ${theme.border}`}></div>
          </div>
        )}

        {/* Team/Player Name Input - hide when existing team is selected, unless readOnly (tournament pre-set teams) */}
        {(!team.existingTeamId || readOnly) && (
          <div className="mb-4">
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>
              {isSinglePlayerMode ? 'Player Name' : 'Team Name'}
            </label>
            <input
              type="text"
              value={team.name || ''}
              onChange={(e) => updateTeamName(teamIndex, e.target.value)}
              disabled={readOnly}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-sm font-semibold ${readOnly ? 'opacity-75 cursor-not-allowed' : ''}`}
              placeholder={isSinglePlayerMode ? 'Select a player' : `Team ${String.fromCharCode(65 + teamIndex)}`}
            />
          </div>
        )}

        {/* Player Count - hide for single player mode when player is selected */}
        {!(isSinglePlayerMode && team.players.length > 0) && (
          <p className={`text-xs ${theme.textMuted} mb-2`}>
            {isSinglePlayerMode ? 'Player' : 'Players'} ({team.players.length}/{maxPlayers})
            {minPlayers > 1 && team.players.length < minPlayers && (
              <span className="text-yellow-500 ml-2">
                (min {minPlayers} required)
              </span>
            )}
          </p>
        )}

        {/* Selected Players List */}
        <div className="space-y-2 mb-3 min-h-[60px]">
          {team.players.length === 0 ? (
            <div className={`text-center py-4 ${theme.textMuted} text-sm italic`}>
              {isSinglePlayerMode ? 'No player selected' : 'No players added yet'}
            </div>
          ) : (
            team.players.map(player => (
              <div
                key={player.id}
                className={`flex items-center justify-between gap-2 p-2 ${theme.card} rounded-lg border ${theme.border} overflow-hidden`}
              >
                <div className="flex items-center gap-x-2 min-w-0">
                  <div className={`w-6 h-6 shrink-0 rounded-full ${theme.backgroundSecondary} ${teamColor.accent} border flex items-center justify-center`}>
                    {player.profileImage ? (
                      <img
                        src={resolveImageUrl(player.profileImage)}
                        alt={player.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className={`w-3 h-3 ${teamColor.text}`} />
                    )}
                  </div>
                  <span className={`text-sm ${theme.text} truncate`}>
                    {player.name}{player.isCurrentUser && <span className="text-blue-500 ml-1">(You)</span>}
                  </span>
                </div>
                {/* Hide remove button when existing team is selected or read-only */}
                {!team.existingTeamId && !readOnly && (
                  <button
                    type="button"
                    onClick={() => removePlayerFromTeam(player, teamIndex)}
                    className="text-red-500 hover:text-red-700 text-xs font-medium"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Player Dropdown - hide when existing team is selected or read-only */}
        {!team.existingTeamId && !isFull && availablePlayers.length > 0 && !readOnly && (
          <select
            onChange={(e) => {
              const playerId = e.target.value;
              const player = availablePlayers.find(
                m => m.id === playerId || m.id === parseInt(playerId)
              );
              if (player) {
                addPlayerToTeam(player, teamIndex);
                e.target.value = '';
              }
            }}
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-sm`}
            defaultValue=""
          >
            <option value="" disabled>{isSinglePlayerMode ? 'Select player...' : `Add player to ${team.name}...`}</option>
            {dedupePlayers(availablePlayers).map(member => (
              <option key={member.id} value={member.id}>
                {member.name}{member.isCurrentUser ? ' (You)' : ''}
              </option>
            ))}
          </select>
        )}

        {isFull && (
          <div className={`flex items-center justify-center gap-1 text-xs ${teamColor.text} font-medium`}>
            <Check className="w-4 h-4" />
            {isSinglePlayerMode ? 'Player selected' : 'Team complete'}
          </div>
        )}
      </div>
    );
  };

  // Get game type label
  const getGameTypeDescription = () => {
    switch (gameType) {
      case 'single': return 'Single Player';
      case 'double': return 'Doubles/Pair';
      case 'team': return 'Team';
      default: return gameType;
    }
  };

  return (
    <div className="space-y-6">
      {/* Settings Info Section - shown when sport/settings passed from parent */}
      {showSettingsInfo && (sportName || gameType) && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-4 border ${theme.border}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sportName && (
              <div>
                <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Sport</label>
                <div className={`px-3 py-2 ${theme.card} rounded-lg border ${theme.border} ${theme.text} text-sm font-medium`}>
                  {sportName}
                </div>
              </div>
            )}
            <div>
              <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Format</label>
              <div className={`px-3 py-2 ${theme.card} rounded-lg border ${theme.border} ${theme.text} text-sm font-medium`}>
                {getGameTypeDescription()}
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>
                {isSinglePlayerMode ? 'Players' : 'Players per Team'}
              </label>
              <div className={`px-3 py-2 ${theme.card} rounded-lg border ${theme.border} ${theme.text} text-sm font-medium`}>
                {isSinglePlayerMode ? '1 (Singles)' : (minPlayers === maxPlayers ? maxPlayers : `${minPlayers} - ${maxPlayers}`)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read-only message - shown only when teams are locked from a tournament */}
      {readOnly && showReadOnlyMessage && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-4 border-2 border-blue-500 mb-4`}>
          <p className={`text-sm ${theme.text} text-center`}>
            ℹ️ Teams are pre-set from the tournament and cannot be modified
          </p>
        </div>
      )}

      {/* Game Type Badge - shown when settings info is not displayed */}
      {!showSettingsInfo && (
        <div className="flex items-center justify-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${theme.backgroundSecondary} ${theme.text} border ${theme.border}`}>
            <Users className="w-3 h-3 inline mr-1" />
            {isSinglePlayerMode ? 'Singles Format' : `Max ${maxPlayers} players per team`}
          </span>
          {availablePlayersCount != null && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
              {availablePlayersCount} available
            </span>
          )}
        </div>
      )}

      {/* Random Teams + Generate Names */}
      {teamCount > 1 && canRandomlyDistribute() && !readOnly && (
        <div className="flex flex-col items-center gap-3">
          {/* Generate Team Names checkbox */}
          <label className={`flex items-center gap-2 cursor-pointer select-none text-sm ${theme.text}`}>
            <input
              type="checkbox"
              checked={generateNames}
              onChange={e => setGenerateNames(e.target.checked)}
              className="w-4 h-4 rounded accent-blue-500"
            />
            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
            Generate team names with AI
          </label>

          <button
            type="button"
            onClick={async () => {
              randomlyDistributePlayers();
              if (!generateNames) return;
              setIsGeneratingNames(true);
              try {
                const result = await apiCall('/teams/generate-names', {
                  method: 'POST',
                  body: JSON.stringify({ count: teamCount, sport: sportName || undefined }),
                });
                if (result?.names?.length) {
                  setTeams(prev => prev.map((team, i) => ({
                    ...team,
                    name: result.names[i] ?? team.name,
                  })));
                }
              } catch {
                // silently fall back to default names
              } finally {
                setIsGeneratingNames(false);
              }
            }}
            disabled={isGeneratingNames}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${theme.secondary} ${theme.secondaryHover} ${theme.text} border ${theme.border} disabled:opacity-60`}
          >
            {isGeneratingNames ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Shuffle className="w-4 h-4" />
                Random Teams
              </>
            )}
          </button>

          {randomWarning && (
            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 max-w-sm text-center">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{randomWarning}</span>
            </div>
          )}
        </div>
      )}

      {/* Teams Grid */}
      {teamCount === 1 ? (
        <div className="max-w-lg mx-auto">
          {teams.map((team, index) =>
            <div key={team.id || `team-${index}`}>{renderTeamCard(team, index)}</div>
          )}
        </div>
      ) : (
        <div className={`grid grid-cols-1 ${teamCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
          {teams.map((team, index) =>
            <div key={team.id || `team-${index}`}>{renderTeamCard(team, index)}</div>
          )}
        </div>
      )}

      {/* Available Players Info */}
      {availablePlayers.length > 0 && (
        <p className={`text-sm ${theme.textMuted} text-center`}>
          {availablePlayers.length} player{availablePlayers.length !== 1 ? 's' : ''} available to assign
        </p>
      )}

      {/* Validation Warning */}
      {!areTeamsValid() && (
        <div className={`flex items-center justify-center gap-2 text-sm text-yellow-500`}>
          <AlertCircle className="w-4 h-4" />
          Each team needs at least {minPlayers} player{minPlayers > 1 ? 's' : ''}
        </div>
      )}

      {/* Save Error */}
      {saveError && (
        <div className={`flex items-center justify-center gap-2 text-sm text-red-500`}>
          <AlertCircle className="w-4 h-4" />
          {saveError}
        </div>
      )}

      {/* Save Toggle */}
      {showSaveToggle && (
        <div className={`flex items-center justify-center gap-2 ${theme.text}`}>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={shouldSave}
              onChange={(e) => setShouldSave(e.target.checked)}
              className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm">
              <Save className="w-4 h-4 inline mr-1" />
              Save team for future use
            </span>
          </label>
        </div>
      )}

      {/* Action Buttons */}
      {(showConfirmButton || actionButton) && (
        <div className="space-y-3">
          {actionButton || (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!areTeamsValid() || isSaving}
              className={`w-full py-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-x-2 ${
                areTeamsValid() && !isSaving
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              <span>{isSaving ? 'Saving...' : confirmButtonText}</span>
            </button>
          )}

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={`w-full py-3 rounded-xl font-medium transition-colors ${theme.card} ${theme.text} border ${theme.border} hover:${theme.cardHover}`}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamFormation;
